import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale/he';
import { useTheme } from 'next-themes';
import { useId } from 'react';
import { calculateAdjustments, calculatePercent, clampPercent } from './price-scale.utils';

interface Price {
  price: number;
  date: string | Date;
}

interface PriceScaleProps {
  meanPrice: number;
  current: Price;
  min: Price;
  max: Price;
}

export default function PriceScale({ meanPrice, current, min, max }: PriceScaleProps) {
  const gradientId = useId();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const minColor = isDark ? '#34d399' : '#10b981';
  const maxColor = isDark ? '#f87171' : '#ef4444';
  const meanColor = isDark ? '#9ca3af' : '#6b7280';
  const lightness = isDark ? 50 : 40;

  const distance = (date: string | Date) => {
    try {
      return formatDistanceToNow(new Date(date), { locale: he });
    } catch {
      return '';
    }
  };

  const currentPercent = clampPercent(calculatePercent(current.price, min.price, max.price));
  const meanPercent = clampPercent(calculatePercent(meanPrice, min.price, max.price));

  const barColor = `hsl(${130 - currentPercent * 1.3}, 100%, ${lightness}%)`;

  const [gapCurrent, gapMean] = calculateAdjustments(currentPercent, meanPercent);

  const currentPriceCx = `${1 + currentPercent * 0.99 + gapCurrent}%`;
  const meanPriceCx = `${1 + meanPercent * 0.99 + gapMean}%`;

  return (
    <div className="w-full">
      <svg width="100%" height="160" xmlns="http://www.w3.org/2000/svg">
        <title>מחירון</title>
        <defs>
          <linearGradient id={`${gradientId}-priceGradient`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        {/* Gradient bar */}
        <rect
          x="0"
          y="50"
          width="100%"
          height="12"
          fill={`url(#${gradientId}-priceGradient)`}
          rx="5"
          ry="5"
        />

        {/* Current price indicator */}
        {max.price === current.price ? (
          <text
            x="100%"
            y="138"
            fontSize="14"
            fill={maxColor}
            fontWeight="bold"
            textAnchor="start"
            fontFamily="Assistant Variable, sans-serif"
          >
            המחיר הנוכחי
          </text>
        ) : min.price === current.price ? (
          <text
            x="0%"
            y="138"
            fontSize="14"
            fill={minColor}
            fontWeight="bold"
            textAnchor="end"
            fontFamily="Assistant Variable, sans-serif"
          >
            המחיר הנוכחי
          </text>
        ) : current.price !== null ? (
          <>
            <circle
              cx={currentPriceCx}
              cy="56"
              r="9"
              fill={barColor}
              stroke="white"
              strokeWidth="2"
            />
            <text
              x={currentPriceCx}
              y="43"
              fontSize="15"
              fontWeight="bold"
              fill={barColor}
              textAnchor="middle"
              fontFamily="Assistant Variable, sans-serif"
            >
              ₪{current.price.toLocaleString()}
            </text>
            <text
              x={currentPriceCx}
              y="28"
              fontSize="14"
              fontWeight="normal"
              fill={barColor}
              textAnchor="middle"
              fontFamily="Assistant Variable, sans-serif"
            >
              מחיר נוכחי
            </text>
          </>
        ) : null}

        {/* Mean price indicator — dashed vertical line */}
        {max.price === Number(meanPrice) ? (
          <text
            x="100%"
            y="138"
            fontSize="14"
            fill={meanColor}
            fontWeight="bold"
            textAnchor="start"
            fontFamily="Assistant Variable, sans-serif"
          >
            המחיר הממוצע
          </text>
        ) : min.price === Number(meanPrice) ? null : (
          <>
            <line
              x1={meanPriceCx}
              y1="38"
              x2={meanPriceCx}
              y2="68"
              stroke={meanColor}
              strokeWidth="3"
              strokeDasharray="4,2"
              opacity="0.9"
            />
            <text
              x={meanPriceCx}
              y="35"
              fontSize="12"
              fontWeight="600"
              fill={meanColor}
              textAnchor="middle"
              fontFamily="Assistant Variable, sans-serif"
            >
              ₪{Number(meanPrice).toLocaleString()}
            </text>
            <text
              x={meanPriceCx}
              y="22"
              fontSize="12"
              fontWeight="normal"
              fill={meanColor}
              textAnchor="middle"
              fontFamily="Assistant Variable, sans-serif"
            >
              מחיר ממוצע
            </text>
          </>
        )}

        {/* Min endpoint */}
        <circle cx="1%" cy="56" r="8" fill={minColor} stroke="white" strokeWidth="2" />
        <text
          x="0%"
          y="90"
          fontSize="14"
          fontWeight="bold"
          fill={minColor}
          textAnchor="end"
          fontFamily="Assistant Variable, sans-serif"
        >
          ₪{min.price.toLocaleString()}
        </text>
        <text
          x="0%"
          y="106"
          fontSize="12"
          fill={minColor}
          textAnchor="end"
          fontFamily="Assistant Variable, sans-serif"
        >
          המחיר הנמוך ביותר
        </text>
        <text
          x="0%"
          y="122"
          fontSize="12"
          fill={minColor}
          textAnchor="end"
          fontFamily="Assistant Variable, sans-serif"
        >
          לפני {distance(min.date)}
        </text>

        {/* Max endpoint */}
        <circle cx="99%" cy="56" r="8" fill={maxColor} stroke="white" strokeWidth="2" />
        <text
          x="100%"
          y="90"
          fontSize="14"
          fill={maxColor}
          fontWeight="bold"
          textAnchor="start"
          fontFamily="Assistant Variable, sans-serif"
        >
          ₪{max.price.toLocaleString()}
        </text>
        <text
          x="100%"
          y="106"
          fontSize="12"
          fill={maxColor}
          textAnchor="start"
          fontFamily="Assistant Variable, sans-serif"
        >
          המחיר היקר ביותר
        </text>
        <text
          x="100%"
          y="122"
          fontSize="12"
          fill={maxColor}
          textAnchor="start"
          fontFamily="Assistant Variable, sans-serif"
        >
          לפני {distance(max.date)}
        </text>
      </svg>
    </div>
  );
}
